import React, { useState, useContext, useEffect, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import moduleConfig from "./moduleConfig";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import { ContainerLoading } from "../../UI/loading/ContainerLoading";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import { isEmpty } from "../../../helpers/CommonHelper";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import {
  updateInitialState,
  updateReducer,
} from "../../../reducers/module/updateReducer";
import {
  loadItemGeneral,
  onSubmitGeneral,
} from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";
import CMSFormMultiSelect from "../../UI/forms/CMSFormMultiSelect";

const FoodUpdate = () => {
  const [updateState, updateDispatch] = useReducer(
    updateReducer,
    updateInitialState
  );
  const [formInitialValues, setFormInitialValues] = useState({});
  const { setBreadcrumb } = useContext(BreadcrumbContext);

  let { id } = useParams();
  let navigate = useNavigate();

  const loadItem = async () => {
    await loadItemGeneral(id, `${moduleConfig.url}/${id}`, updateDispatch);
  };
  useEffect(() => {
    loadItem();
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      // moduleConfig.breadcrumbs.details,
      moduleConfig.breadcrumbs.update,
    ]);
  }, [id]);

  useEffect(() => {
    if (updateState.data.item && updateState.data.item._id) {
      setFormInitialValues(updateState.data.item);
    }
  }, [updateState.data]);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {updateState.status === "fetching" && updateState.isLoading && (
        <ContainerLoading />
      )}
      {updateState.error && updateState.status === "fetchFailed" ? (
        <BlockErrorMessage error={updateState.error} />
      ) : (
        <>
          <ModuleHeader
            moduleTitle={`Update ${moduleConfig.singleItemTitle}`}
            moduleUrl={moduleConfig.url}
          ></ModuleHeader>

          {Object.keys(formInitialValues).length > 0 &&
            updateState.data &&
            updateState.data.item &&
            !isEmpty(updateState.data.item._id) && (
              <section className="content">
                <Card>
                  <Card.Body>
                    {updateState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        ...formInitialValues,
                        file: updateState.data.item.image,
                        slideImageFile: updateState.data.item.slideImage,
                      }}
                      validationSchema={yup.object({
                        title: yup
                          .string()
                          .required("Title is required.")
                          .max(120, "Title should be at most 120 characters"),
                        allergens: yup
                          .array()
                          .of(yup.string())
                          .min(1, "Please select at least one allergen"),

                        foodTime: yup
                          .string()
                          .required("Food time is required."),
                        hotOrCold: yup.string().required(),
                        vegOrNonVeg: yup
                          .string()
                          .required("Food type is required."),
                        file: yup
                          .mixed()
                          .required("Image is required")
                          .imageTypeCheck(
                            moduleConfig.imageRequirement.allowedExtensions
                          )
                          .imageSizeCheck(moduleConfig.imageRequirement.size),
                        description: yup
                          .string(),
                        nutritionalInfo: yup.string(),
                        ingredients: yup.string(),
                        sortOrder: yup
                          .number()
                          .required("Sort order is required."),
                        status: yup.string().required("Status is required."),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        const formData = new FormData();
                        for (let name in values) {
                          if (name === "allergens") {
                            if (Array.isArray(values[name])) {
                              for (var i = 0; i < values[name].length; i++) {
                                formData.append(name + "[]", values[name][i]);
                              }
                            }
                          } else if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          `${moduleConfig.url}/${id}`,
                          "back",
                          updateDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {/* {console.log(form.errors)} */}
                          {updateState.error && (
                            <FormErrorMessage error={updateState.error} />
                          )}

                          <CMSFormInput
                            name="title"
                            label="Title"
                            required={true}
                            form={form}
                          />

                          <CMSFormMultiSelect
                            name="allergens"
                            label="Allergens"
                            selectionValue="value"
                            selected={form.values.allergens}
                            required={false}
                            form={form}
                            options={
                              updateState.data && updateState.data.allergens
                                ? updateState.data.allergens
                                : []
                            }
                            displayValue="label"
                          />

                          <CMSFormSelect
                            name="foodTime"
                            label="Food Time"
                            required={true}
                            form={form}
                            options={[
                              { label: "Breakfast", value: "Breakfast" },
                              { label: "Lunch", value: "Lunch" },
                              {
                                label: "Afternoon Snack",
                                value: "Afternoon Snack",
                              },
                              { label: "Dinner", value: "Dinner" },
                            ]}
                          />
                          <CMSFormSelect
                            name="hotOrCold"
                            label="Hot Or Cold"
                            form={form}
                            required={true}
                            options={[
                              { label: "Hot", value: "Hot" },
                              { label: "Cold", value: "Cold" },
                            ]}
                          />
                          <CMSFormSelect
                            name="vegOrNonVeg"
                            label="Veg Or Non veg"
                            required={true}
                            form={form}
                            options={[
                              { label: "Veg", value: "Veg" },
                              { label: "Non Veg", value: "Non Veg" },
                            ]}
                          />
                          <CMSFormInput
                            name="ingredients"
                            label="Ingredients"
                            info="Add texts in a comma separated way"
                            required={false}
                            form={form}
                          />

                          <CMSFormRichText
                            name="description"
                            label="Description"
                            form={form}
                            required={false}
                          />

                          <CMSFormImageWithCrop
                            label="Image"
                            name="file"
                            info="Recommended dimensions 500*500"
                            aspect={1 / 1}
                            outputSize={imageRequirement.dimension}
                            form={form}
                            required={true}
                          />

                          <CMSFormInput
                            name="nutritionalInfo"
                            label="Nutritional Info"
                            info="Add texts in a comma separated way"
                            required={false}
                            form={form}
                          />

                          <CMSFormInput
                            name="sortOrder"
                            label="Sort Order"
                            form={form}
                            required={true}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />
                          <button className="btn btn-primary" type="submit">
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            )}
        </>
      )}
    </DashboardLayout>
  );
};
export default FoodUpdate;
